import { styled } from "@mui/material";
import * as monaco from "monaco-editor";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useDiffEditor from "../../hooks/useDiffEditor";
import { copyToClipboard, getOrCreateModel } from "./utils";

const DiffEditorContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "height" && prop !== "width",
})<{ height: string | number; width: string | number }>(({ height, width }) => ({
  height,
  width,
}));

const isFinalLineEmpty = (model: monaco.editor.ITextModel) => {
  const finalLine = model.getLineContent(model.getLineCount());
  return finalLine === "";
};

const addEndOfLine = (model?: monaco.editor.ITextModel) => {
  const newLineNumber = (model?.getLineCount() || 0) + 1;
  const range = new monaco.Range(newLineNumber, 1, newLineNumber, 1);
  model?.applyEdits([
    {
      range,
      text: model?.getEOL() || "\n",
    },
  ]);
};

interface IMonacoDiffEditorProps {
  modified: string;
  original: string;
  originalModelPath: string;
  modifiedModelPath: string;
  position: number;
  height: string | number;
  width: string | number;
  modifiedMarkers?: monaco.editor.IMarkerData[];
  options?: monaco.editor.IDiffEditorOptions;
  onChange?: (editorValue: string, event: monaco.editor.IModelContentChangedEvent) => void;
  language?: string;
}

const DiffEditor: FC<IMonacoDiffEditorProps> = ({
  modified,
  original,
  originalModelPath,
  modifiedModelPath,
  position,
  height,
  width,
  options = {},
  modifiedMarkers,
  onChange,
  language = "sbs",
}) => {
  const modifiedValueRef = useRef<string>(modified);
  const subscription = useRef<monaco.IDisposable | null | undefined>(null);
  const hasAppliedPermalinkSelection = useRef(false);
  const searchParamsRef = useRef(new URLSearchParams());
  const [searchParams, setSearchParams] = useSearchParams();

  const [diffInfo, setDiffInfo] = useState<monaco.editor.ILineChange[] | undefined>(undefined);

  const { editorRef, isEditorReady, monacoEl } = useDiffEditor({
    options,
    onDidUpdateDiff: (editor) => {
      setDiffInfo(editor?.getLineChanges() || undefined);
    },
  });

  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  useEffect(() => {
    hasAppliedPermalinkSelection.current = false;
  }, [searchParams]);

  useEffect(() => {
    if (!position) return;
    editorRef.current?.setPosition({ lineNumber: position, column: 1 });
    editorRef.current?.focus();
  }, [position, editorRef]);

  useEffect(() => {
    const originalModel = getOrCreateModel(original, language, originalModelPath);

    const modifiedModel = getOrCreateModel(modified, language, modifiedModelPath);

    editorRef.current?.setModel({
      original: originalModel,
      modified: modifiedModel,
    });
  }, [editorRef, language, modified, modifiedModelPath, original, originalModelPath]);

  useEffect(() => {
    const diffEditor = editorRef.current;
    if (diffEditor) {
      const originalModel = diffEditor?.getModel()?.original;
      const modifiedModel = diffEditor?.getModel()?.modified;

      if (modifiedModel && !isFinalLineEmpty(modifiedModel)) {
        addEndOfLine(modifiedModel);
      }

      if (originalModel && !isFinalLineEmpty(originalModel)) {
        addEndOfLine(originalModel);
      }
    }
  }, [editorRef, diffInfo]);

  useEffect(() => {
    const originalEditor = editorRef.current?.getModel()?.original;
    if (!originalEditor) return;
    originalEditor.setValue(original);
  }, [original, editorRef]);

  useEffect(() => {
    const modifiedEditor = editorRef.current?.getModifiedEditor();
    const model = modifiedEditor?.getModel();
    if (!modifiedEditor || !model) return;
    if (modifiedEditor.getOption(monaco.editor.EditorOption.readOnly)) {
      modifiedEditor.setValue(modified);
    } else {
      if (modified !== modifiedEditor.getValue()) {
        modifiedEditor.executeEdits("", [
          {
            range: model.getFullModelRange(),
            text: modified,
            forceMoveMarkers: true,
          },
        ]);
      }
    }
  }, [modified, editorRef]);

  useEffect(() => {
    modifiedValueRef.current = modified;
  }, [modified]);

  useEffect(() => {
    if (isEditorReady && onChange) {
      subscription.current?.dispose();
      const modifiedModel = editorRef.current?.getModel()?.modified;

      subscription.current = modifiedModel?.onDidChangeContent((event) => {
        const modifiedValue = modifiedModel?.getValue();

        if (modifiedValue && modifiedValueRef.current !== modifiedValue) {
          onChange(modifiedValue, event);
        }
      });
    }
  }, [isEditorReady, onChange, editorRef]);

  useEffect(() => {
    const modifiedModel = editorRef.current?.getModel()?.modified;
    if (!modifiedModel || !modifiedMarkers || !isEditorReady) return;

    monaco.editor.setModelMarkers(modifiedModel, "", modifiedMarkers);
  }, [editorRef, isEditorReady, modifiedMarkers]);

  const setSelectedText = useCallback(() => {
    const lines = searchParams.get("lines");

    if (!lines || !isEditorReady || !editorRef.current || hasAppliedPermalinkSelection.current) return;

    const modifiedEditor = editorRef.current.getModifiedEditor();
    const model = modifiedEditor.getModel();
    if (!model || model.getLineCount() === 0) return;

    const linesAux = lines.split("-");
    const rowFrom = linesAux[0];
    const rowTo = linesAux[1];
    const colFrom = linesAux[2];
    const colTo = linesAux[3];

    editorRef.current.setSelection({
      startLineNumber: rowFrom ? parseInt(rowFrom) : 1,
      startColumn: colFrom ? parseInt(colFrom) : 1,
      endLineNumber: rowTo ? parseInt(rowTo) : 1,
      endColumn: colTo ? parseInt(colTo) : 1,
    });
    editorRef.current.revealLineInCenter(rowFrom ? parseInt(rowFrom) : 1);
    editorRef.current.focus();

    hasAppliedPermalinkSelection.current = true;
  }, [searchParams, isEditorReady, editorRef]);

  useEffect(() => {
    if (!isEditorReady || !editorRef) return;
    editorRef.current?.updateOptions(options);

    setSelectedText();

    if (!editorRef.current?.getModifiedEditor().getAction("copy-link-to-clipboard")) {
      editorRef.current?.addAction({
        id: "copy-link-to-clipboard",
        label: "Copy permalink to code block",
        contextMenuGroupId: "navigation",
        run: copyToClipboard,
      });
    }

    const disposable = editorRef.current?.getModifiedEditor().onDidChangeCursorSelection((e) => {
      if (e.source !== "api" && e.source !== "modelChange" && searchParamsRef.current.has("lines")) {
        setSearchParams((params) => {
          params.delete("lines");
          return params;
        });
      }
    });

    return () => disposable?.dispose();
  }, [editorRef, options, isEditorReady, setSelectedText, setSearchParams]);

  const replaceLinesSymmetric = (
    text: string,
    editor?: monaco.editor.IStandaloneCodeEditor,
    line?: number,
    column?: number
  ) => {
    const actualLine = line || 1;
    const actualColumn = column || 1;

    const range = new monaco.Range(actualLine, actualColumn, actualLine, actualColumn);
    editor?.executeEdits("", [
      {
        range,
        text,
        forceMoveMarkers: true,
      },
    ]);
  };

  const defaultReplaceLines = (
    text: string,
    model: monaco.editor.ITextModel,
    startLineNumber: number,
    endLineNumber: number,
    editor?: monaco.editor.IStandaloneCodeEditor
  ) => {
    const column = model.getLineMaxColumn(endLineNumber);

    const range = new monaco.Range(startLineNumber, 1, endLineNumber, column);
    editor?.executeEdits("", [
      {
        range,
        text,
        forceMoveMarkers: true,
      },
    ]);
  };

  const replaceModifiedEditorTextWithEmptyString = (
    currentDiff: monaco.editor.ILineChange,
    modiifedEditor?: monaco.editor.IStandaloneCodeEditor
  ) => {
    const range = new monaco.Range(currentDiff.modifiedStartLineNumber, 1, currentDiff.modifiedEndLineNumber + 1, 1);
    modiifedEditor?.executeEdits("", [
      {
        range,
        text: "",
        forceMoveMarkers: true,
      },
    ]);
  };

  const keepOriginalChange = useCallback(
    (currentDiff: monaco.editor.ILineChange) => {
      const diffEditor = editorRef.current;
      const originalModel = diffEditor?.getModel()?.original;
      const modifiedModel = diffEditor?.getModel()?.modified;
      const modifiedEditor = diffEditor?.getModifiedEditor();
      const lineFeed = modifiedEditor?.getModel()?.getEOL() || "\n";
      if (!currentDiff || !diffEditor || !originalModel || !modifiedModel) return;

      const startLineNumber = currentDiff.originalStartLineNumber;
      const startLineColumn = 1;
      let endLineNumber = currentDiff.originalEndLineNumber;

      let endLineColumn: number | undefined = undefined;
      if (endLineNumber) {
        endLineColumn = originalModel.getLineMaxColumn(endLineNumber);
      }

      if (!endLineColumn) {
        // If the end line column cannot be determined, revert upto the first column of the line after the end line
        endLineColumn = 1;
        endLineNumber += 1;
      }

      const range = new monaco.Range(startLineNumber, startLineColumn, endLineNumber, endLineColumn);
      const originalText = originalModel.getValueInRange(range);

      const isLineDeletion = currentDiff.modifiedEndLineNumber === 0;
      const isLineAddition = currentDiff.originalEndLineNumber === 0;

      if (isLineDeletion) {
        const isTheFirstLine = currentDiff.modifiedStartLineNumber === 0;
        if (isTheFirstLine) {
          replaceLinesSymmetric(originalText + lineFeed, modifiedEditor);
        } else {
          const column = modifiedModel.getLineMaxColumn(currentDiff.modifiedStartLineNumber);

          replaceLinesSymmetric(lineFeed + originalText, modifiedEditor, currentDiff.modifiedStartLineNumber, column);
        }
      } else if (isLineAddition) {
        replaceModifiedEditorTextWithEmptyString(currentDiff, modifiedEditor);
      } else {
        defaultReplaceLines(
          originalText,
          modifiedModel,
          currentDiff.modifiedStartLineNumber,
          currentDiff.modifiedEndLineNumber,
          modifiedEditor
        );
      }
    },
    [editorRef]
  );

  const replaceOriginalEditorTextWithEmptyString = (
    currentDiff: monaco.editor.ILineChange,
    originalModel: monaco.editor.ITextModel,
    originalEditor?: monaco.editor.IStandaloneCodeEditor
  ) => {
    const isTheFirstLine = currentDiff.originalStartLineNumber - 1 === 0;
    if (isTheFirstLine) {
      const range = new monaco.Range(1, 1, currentDiff.originalEndLineNumber + 1, 1);
      originalEditor?.executeEdits("", [
        {
          range,
          text: "",
          forceMoveMarkers: true,
        },
      ]);
    } else {
      const column1 = originalModel.getLineMaxColumn(currentDiff.originalStartLineNumber - 1);

      const column2 = originalModel.getLineMaxColumn(currentDiff.originalEndLineNumber);

      const range = new monaco.Range(
        currentDiff.originalStartLineNumber - 1,
        column1,
        currentDiff.originalEndLineNumber,
        column2
      );
      originalEditor?.executeEdits("diffEditor", [
        {
          range,
          text: "",
        },
      ]);
    }
  };

  const keepModifiedChange = useCallback(
    (currentDiff: monaco.editor.ILineChange) => {
      const diffEditor = editorRef.current;
      const originalModel = diffEditor?.getModel()?.original;
      const modifiedModel = diffEditor?.getModel()?.modified;
      const originalEditor = diffEditor?.getOriginalEditor();
      const lineFeed = originalEditor?.getModel()?.getEOL() || "\n";
      if (!currentDiff || !diffEditor || !originalModel || !modifiedModel) return;

      const startLineNumber = currentDiff.modifiedStartLineNumber;
      const startLineColumn = 1;
      let endLineNumber = currentDiff.modifiedEndLineNumber;

      let endLineColumn: number | undefined = undefined;
      if (endLineNumber) {
        endLineColumn = modifiedModel.getLineMaxColumn(endLineNumber);
      }

      if (!endLineColumn) {
        // If the end line column cannot be determined, make changes upto the first column of the line after the end line
        endLineColumn = 1;
        endLineNumber += 1;
      }

      const range = new monaco.Range(startLineNumber, startLineColumn, endLineNumber, endLineColumn);

      const replaceText = modifiedModel.getValueInRange(range);

      const isLineDeletion = currentDiff.modifiedEndLineNumber === 0;
      const isLineAddition = currentDiff.originalEndLineNumber === 0;

      if (isLineAddition) {
        const isTheFirstLine = currentDiff.originalStartLineNumber === 0;
        if (isTheFirstLine) {
          replaceLinesSymmetric(replaceText + lineFeed, originalEditor);
        } else {
          const column = originalModel.getLineMaxColumn(currentDiff.originalStartLineNumber);
          replaceLinesSymmetric(lineFeed + replaceText, originalEditor, currentDiff.originalStartLineNumber, column);
        }
      } else if (isLineDeletion) {
        replaceOriginalEditorTextWithEmptyString(currentDiff, originalModel, originalEditor);
      } else {
        defaultReplaceLines(
          replaceText,
          originalModel,
          currentDiff.originalStartLineNumber,
          currentDiff.originalEndLineNumber,
          originalEditor
        );
      }
    },
    [editorRef]
  );

  const keepBothChanges = useCallback(
    (currentDiff: monaco.editor.ILineChange) => {
      const diffEditor = editorRef.current;
      const originalModel = diffEditor?.getModel()?.original;
      const modifiedModel = diffEditor?.getModel()?.modified;
      const originalEditor = diffEditor?.getOriginalEditor();
      const modifiedEditor = diffEditor?.getModifiedEditor();
      const lineFeed = originalEditor?.getModel()?.getEOL() || "\n";
      if (!currentDiff || !diffEditor || !originalModel || !modifiedModel || !modifiedEditor || !originalEditor) return;

      const { endLineNumber: originalEndLineNumber, endColumn: originalEndColumn } = originalModel.getFullModelRange();

      const { endLineNumber: modifiedEndLineNumber, endColumn: modifiedEndColumn } = modifiedModel.getFullModelRange();

      const originalDiffRange = new monaco.Range(
        currentDiff.originalStartLineNumber,
        1,
        currentDiff.originalEndLineNumber,
        originalModel.getLineMaxColumn(currentDiff.originalEndLineNumber)
      );
      const originalDiffText = originalModel.getValueInRange(originalDiffRange);

      const originalRemainingRange = new monaco.Range(
        currentDiff.originalEndLineNumber + 1,
        1,
        originalEndLineNumber,
        originalEndColumn
      );

      const originalRemainingText = originalModel.getValueInRange(originalRemainingRange);

      const modifiedDiffRange = new monaco.Range(
        currentDiff.modifiedStartLineNumber,
        1,
        currentDiff.modifiedEndLineNumber,
        modifiedModel.getLineMaxColumn(currentDiff.modifiedEndLineNumber)
      );

      const modifiedDiffText = modifiedModel.getValueInRange(modifiedDiffRange);

      const modifiedRemainingRange = new monaco.Range(
        currentDiff.modifiedEndLineNumber + 1,
        1,
        modifiedEndLineNumber,
        modifiedEndColumn
      );

      const modifiedRemainingText = modifiedModel.getValueInRange(modifiedRemainingRange);

      const diffText = originalDiffText + lineFeed + modifiedDiffText;

      const originalReplaceText = diffText + lineFeed + originalRemainingText;

      const modifiedReplaceText = diffText + lineFeed + modifiedRemainingText;

      const originalEditRange = new monaco.Range(
        currentDiff.originalStartLineNumber,
        1,
        originalEndLineNumber,
        originalEndColumn
      );

      const modifiedEditRange = new monaco.Range(
        currentDiff.modifiedStartLineNumber,
        1,
        modifiedEndLineNumber,
        modifiedEndColumn
      );

      originalEditor.executeEdits("", [
        {
          range: originalEditRange,
          text: originalReplaceText,
          forceMoveMarkers: true,
        },
      ]);

      modifiedEditor.executeEdits("", [
        {
          range: modifiedEditRange,
          text: modifiedReplaceText,
          forceMoveMarkers: true,
        },
      ]);
    },
    [editorRef]
  );

  const codeLensRef = useRef<monaco.IDisposable | null>(null);

  useEffect(() => {
    const diffEditor = editorRef.current;
    diffEditor?.addAction({
      id: "disableUndo",
      label: "disableUndo",
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyZ],
      run: () => {},
    });
  }, [editorRef]);

  useEffect(() => {
    monaco.editor.registerCommand("keepOriginalChange", (_accesor, args) => {
      keepOriginalChange(args);
    });
    monaco.editor.registerCommand("keepModifiedChange", (_accesor, args) => {
      keepModifiedChange(args);
    });
    monaco.editor.registerCommand("keepBothChanges", (_accesor, args) => {
      keepBothChanges(args);
    });
  }, [keepBothChanges, keepModifiedChange, keepOriginalChange]);

  useEffect(() => {
    if (isEditorReady && diffInfo && !options.readOnly) {
      if (codeLensRef.current) codeLensRef.current.dispose();
      codeLensRef.current = monaco.languages.registerCodeLensProvider("sbs", {
        provideCodeLenses: () => {
          const codeLenses: monaco.languages.CodeLens[] = [];
          diffInfo.forEach((diff) => {
            const range = new monaco.Range(
              diff.modifiedStartLineNumber + (diff.modifiedEndLineNumber === 0 ? 1 : 0),
              1,
              diff.modifiedEndLineNumber || diff.modifiedStartLineNumber + 1,
              1
            );
            const keepOriginalChangeCodeLens = {
              range,
              id: `keepOriginalChange-${diff.originalStartLineNumber}`,
              command: {
                id: "keepOriginalChange",
                title: "Accept Published Changes",
                arguments: [diff],
              },
            };
            codeLenses.push(keepOriginalChangeCodeLens);

            const keepModifiedChangeCodeLens = {
              range,
              id: `keepModifiedChange-${diff.originalStartLineNumber}`,
              command: {
                id: "keepModifiedChange",
                title: "Accept Current Draft Changes",
                arguments: [diff],
              },
            };

            codeLenses.push(keepModifiedChangeCodeLens);

            const isLineDeletionOrAddition = diff.originalEndLineNumber === 0 || diff.modifiedEndLineNumber === 0;
            if (!isLineDeletionOrAddition) {
              const keepBothChangesCodeLens = {
                range,
                id: `keepBothChanges-${diff.originalStartLineNumber}`,
                command: {
                  id: "keepBothChanges",
                  title: "Keep Both Changes",
                  arguments: [diff],
                },
              };
              codeLenses.push(keepBothChangesCodeLens);
            }
          });
          return {
            lenses: codeLenses,
            dispose: () => {
              codeLensRef.current?.dispose();
            },
          };
        },
        resolveCodeLens: function (model, codeLens) {
          return codeLens;
        },
      });
    }
  }, [isEditorReady, diffInfo, editorRef, keepBothChanges, keepModifiedChange, keepOriginalChange, options.readOnly]);

  return <DiffEditorContainer height={height} width={width} ref={monacoEl} />;
};

export default DiffEditor;
